import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, Pencil, X, Building2 } from "lucide-react";

const empty = { name: "", slug: "", active: true };

export default function AdminBranches() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/admin/branches").then((r) => setItems(r.data || []));

  useEffect(() => {
    load().catch(() => toast.error("No se pudieron cargar las sucursales"));
  }, []);

  const startNew = () => {
    setForm(empty);
    setEditing(null);
    setOpen(true);
  };

  const startEdit = (b) => {
    setForm({ name: b.name, slug: b.slug, active: b.active !== false });
    setEditing(b.id);
    setOpen(true);
  };

  const maxBranches = user?.max_branches;
  const atQuota = maxBranches != null && items.length >= maxBranches;
  const quotaLabel =
    maxBranches == null
      ? `${items.length} sucursales — ${user?.plan_name || "Plan"}`
      : `${items.length} de ${maxBranches} sucursales — ${user?.plan_name || "Plan"}`;

  const save = async () => {
    try {
      const body = {
        name: form.name.trim(),
        active: form.active,
      };
      if (form.slug?.trim()) body.slug = form.slug.trim();
      if (editing) await api.put(`/admin/branches/${editing}`, body);
      else await api.post("/admin/branches", body);
      toast.success("Sucursal guardada");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#E63946]">Maestro</div>
          <h1 className="font-display text-4xl text-white mt-1">Sucursales</h1>
          <p className="text-white/60 text-sm mt-1">{quotaLabel}</p>
        </div>
        <button type="button" onClick={startNew} className="btn-primary" disabled={atQuota}>
          <Plus size={16} /> Nueva sucursal
        </button>
      </div>

      <div className="glass rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-white/40 border-b border-white/10">
              <th className="p-4">Nombre</th>
              <th className="p-4">Slug</th>
              <th className="p-4">Estado</th>
              <th className="p-4 w-24" />
            </tr>
          </thead>
          <tbody>
            {items.map((b) => (
              <tr key={b.id} className="border-b border-theme-secondary/10 hover:bg-white/[0.02]">
                <td className="p-4 text-white flex items-center gap-2">
                  <Building2 size={16} className="text-[#E63946]" />
                  {b.name}
                </td>
                <td className="p-4 text-white/60 font-mono text-xs">{b.slug}</td>
                <td className="p-4">
                  <span className={b.active !== false ? "text-green-400" : "text-white/40"}>
                    {b.active !== false ? "Activa" : "Inactiva"}
                  </span>
                </td>
                <td className="p-4">
                  <button type="button" onClick={() => startEdit(b)} className="text-white/60 hover:text-white">
                    <Pencil size={16} />
                  </button>
                </td>
              </tr>
            ))}
            {!items.length && (
              <tr>
                <td colSpan={4} className="p-8 text-center text-white/40">
                  No hay sucursales registradas.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="glass w-full max-w-md p-6 rounded-sm space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl text-white">
                {editing ? "Editar sucursal" : "Nueva sucursal"}
              </h2>
              <button type="button" onClick={() => setOpen(false)} className="text-white/60 hover:text-white">
                <X size={20} />
              </button>
            </div>
            <label className="block space-y-1">
              <span className="text-xs text-white/60">Nombre</span>
              <input
                className="input-dark w-full"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ej. Pachuca"
              />
            </label>
            <label className="block space-y-1">
              <span className="text-xs text-white/60">Slug (opcional)</span>
              <input
                className="input-dark w-full font-mono text-sm"
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                placeholder="pachuca"
              />
            </label>
            <label className="flex items-center gap-2 text-sm text-white/60">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
              />
              Sucursal activa
            </label>
            <button type="button" onClick={save} className="btn-primary w-full justify-center">
              Guardar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
